import { deleteObject, ref } from 'firebase/storage';
import { db, storage } from '../Firebase/firebaseConfig';
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  DocumentSnapshot,
  startAfter,
  deleteDoc,
  doc,
  getDoc
} from 'firebase/firestore';
import { EventFormData } from '../Forms/schemas/eventFormSchema';

// Interface extending Zod inferred type; tag mandatory here (no ?)
export interface EventItem extends EventFormData {
  id: string;
  booth:string;
  location:string;
  date:string;
  tag: string;  // mandatory string, no optional
  createdAt?: string;
  updatedAt?: string;
}

// Fetch paginated events with default empty string fallback for tag
export const getEventsFromFirebase = async (
  itemLimit: number,
  lastDoc?: DocumentSnapshot | null
): Promise<{ events: EventItem[]; lastVisible: DocumentSnapshot | null }> => {
  try {
    const eventsCollection = collection(db, 'events');

    const q = lastDoc
      ? query(eventsCollection, orderBy('updatedAt', 'desc'), startAfter(lastDoc), limit(itemLimit))
      : query(eventsCollection, orderBy('updatedAt', 'desc'), limit(itemLimit));

    const querySnapshot = await getDocs(q);
    const events: EventItem[] = [];

    querySnapshot.forEach((docSnap) => {
      const data = docSnap.data();

      events.push({
        id: docSnap.id,
        title: data.title || '',
        booth: data.booth || '',
        location: data.location || '',
        date: data.date || '',
        tag: data.tag || '',           // fallback empty string to ensure mandatory
      });
    });

    const lastVisible = querySnapshot.docs.length > 0
      ? querySnapshot.docs[querySnapshot.docs.length - 1]
      : null;

    return { events, lastVisible };
  } catch (error) {
    console.error("Error fetching events from Firebase:", error);
    return { events: [], lastVisible: null };
  }
};

// Helper to get Firebase Storage path from URL
const getStoragePathFromUrl = (url: string): string | null => {
  try {
    const decodedUrl = decodeURIComponent(url.split('?')[0]);
    const pathMatch = decodedUrl.match(/\/o\/(.+)$/);
    return pathMatch ? pathMatch[1] : null;
  } catch {
    return null;
  }
};

// Delete event by ID (Firestore + Image)
export const deleteEventById = async (id: string): Promise<void> => {
  try {
    const eventRef = doc(db, 'events', id);
    const eventSnap = await getDoc(eventRef);

    if (!eventSnap.exists()) {
      throw new Error('Event not found');
    }

    const eventData = eventSnap.data();
    const urls: string[] = [];

    if (eventData?.image) urls.push(eventData.image);

    const deletePromises = urls.map((url) => {
      const path = getStoragePathFromUrl(url);
      if (path) {
        const imageRef = ref(storage, path);
        return deleteObject(imageRef).catch((err) => {
          console.warn(`Failed to delete image at ${path}:`, err);
        });
      }
      return Promise.resolve();
    });

    await Promise.all(deletePromises);
    await deleteDoc(eventRef);

    console.log(`Event ${id} and associated images deleted successfully.`);
  } catch (error) {
    console.error("Error deleting event and its image:", error);
    throw new Error("Failed to delete event and its image");
  }
};

export default getEventsFromFirebase;
